import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
  type Placement,
  type ReferenceElement,
} from "@floating-ui/dom";

export type FloatingTooltipPlacement = "top" | "bottom";

export interface FloatingTooltipOptions {
  readonly preferredPlacement: FloatingTooltipPlacement;
  readonly offsetPx?: number;
  readonly paddingPx?: number;
  readonly minWidthPx?: number;
  readonly minMaxWidthPx?: number;
}

export type FloatingTooltipCleanup = () => void;

const FLOATING_TOOLTIP_STRATEGY = "fixed";
const DEFAULT_FLOATING_TOOLTIP_OFFSET_PX = 8;
const DEFAULT_FLOATING_TOOLTIP_PADDING_PX = 12;
const DEFAULT_FLOATING_TOOLTIP_MIN_WIDTH_PX = 0;
const DEFAULT_FLOATING_TOOLTIP_MIN_MAX_WIDTH_PX = 180;
const FLOATING_TOOLTIP_ARROW_X_PROPERTY = "--hover-idle-tooltip-arrow-x";

const floatingTooltipVersions = new WeakMap<HTMLElement, number>();

interface ResolvedFloatingTooltipOptions {
  readonly preferredPlacement: FloatingTooltipPlacement;
  readonly offsetPx: number;
  readonly paddingPx: number;
  readonly minWidthPx: number;
  readonly minMaxWidthPx: number;
}

interface FloatingTooltipPosition {
  readonly x: number;
  readonly y: number;
  readonly placement: Placement;
}

export function startFloatingTooltip(
  reference: ReferenceElement,
  tooltip: HTMLElement,
  options: FloatingTooltipOptions,
): FloatingTooltipCleanup {
  const version = activateFloatingTooltipUpdate(tooltip);
  let updateFrame: number | null = null;

  const scheduleUpdate = (): void => {
    if (updateFrame !== null) return;
    updateFrame = requestAnimationFrame(() => {
      updateFrame = null;
      void updateFloatingTooltipForVersion(
        reference,
        tooltip,
        options,
        version,
      );
    });
  };

  void updateFloatingTooltipForVersion(reference, tooltip, options, version);
  const cleanupAutoUpdate = autoUpdate(reference, tooltip, scheduleUpdate);

  return () => {
    cleanupAutoUpdate();
    if (updateFrame !== null) {
      cancelAnimationFrame(updateFrame);
      updateFrame = null;
    }
    cancelFloatingTooltipUpdate(tooltip);
    resetFloatingTooltip(tooltip);
  };
}

export function updateFloatingTooltip(
  reference: ReferenceElement,
  tooltip: HTMLElement,
  options: FloatingTooltipOptions,
): Promise<void> {
  const version = activateFloatingTooltipUpdate(tooltip);
  return updateFloatingTooltipForVersion(reference, tooltip, options, version);
}

export function resetFloatingTooltip(tooltip: HTMLElement): void {
  tooltip.style.position = "";
  tooltip.style.left = "";
  tooltip.style.top = "";
  tooltip.style.maxWidth = "";
  tooltip.style.removeProperty(FLOATING_TOOLTIP_ARROW_X_PROPERTY);
  delete tooltip.dataset.placement;
}

function updateFloatingTooltipForVersion(
  reference: ReferenceElement,
  tooltip: HTMLElement,
  options: FloatingTooltipOptions,
  version: number,
): Promise<void> {
  const resolved = resolveFloatingTooltipOptions(options);
  tooltip.style.position = FLOATING_TOOLTIP_STRATEGY;

  return resolveFloatingTooltipPosition(reference, tooltip, resolved).then(
    (position) => {
      if (!isCurrentFloatingTooltipUpdate(tooltip, version)) return;
      applyFloatingTooltipPosition(reference, tooltip, position);
    },
  );
}

function resolveFloatingTooltipPosition(
  reference: ReferenceElement,
  tooltip: HTMLElement,
  options: ResolvedFloatingTooltipOptions,
): Promise<FloatingTooltipPosition> {
  return computePosition(reference, tooltip, {
    placement: options.preferredPlacement,
    strategy: FLOATING_TOOLTIP_STRATEGY,
    middleware: [
      offset(options.offsetPx),
      flip({ padding: options.paddingPx }),
      shift({ padding: options.paddingPx }),
      size({
        padding: options.paddingPx,
        apply({ availableWidth, elements }) {
          elements.floating.style.maxWidth = toCssPx(
            resolveFloatingTooltipMaxWidth(availableWidth, options),
          );
        },
      }),
    ],
  });
}

function applyFloatingTooltipPosition(
  reference: ReferenceElement,
  tooltip: HTMLElement,
  position: FloatingTooltipPosition,
): void {
  tooltip.style.position = FLOATING_TOOLTIP_STRATEGY;
  tooltip.style.left = toCssPx(position.x);
  tooltip.style.top = toCssPx(position.y);
  tooltip.dataset.placement = normalizeFloatingTooltipPlacement(
    position.placement,
  );
  tooltip.style.setProperty(
    FLOATING_TOOLTIP_ARROW_X_PROPERTY,
    toCssPx(resolveFloatingTooltipArrowX(reference, position.x)),
  );
}

function resolveFloatingTooltipArrowX(
  reference: ReferenceElement,
  tooltipX: number,
): number {
  const referenceRect = reference.getBoundingClientRect();
  return referenceRect.left + referenceRect.width / 2 - tooltipX;
}

function activateFloatingTooltipUpdate(tooltip: HTMLElement): number {
  const version = (floatingTooltipVersions.get(tooltip) ?? 0) + 1;
  floatingTooltipVersions.set(tooltip, version);
  return version;
}

function cancelFloatingTooltipUpdate(tooltip: HTMLElement): void {
  activateFloatingTooltipUpdate(tooltip);
}

function isCurrentFloatingTooltipUpdate(
  tooltip: HTMLElement,
  version: number,
): boolean {
  return floatingTooltipVersions.get(tooltip) === version;
}

function resolveFloatingTooltipOptions(
  options: FloatingTooltipOptions,
): ResolvedFloatingTooltipOptions {
  return {
    preferredPlacement: options.preferredPlacement,
    offsetPx: resolveNonNegativePixelOption(
      options.offsetPx,
      DEFAULT_FLOATING_TOOLTIP_OFFSET_PX,
    ),
    paddingPx: resolveNonNegativePixelOption(
      options.paddingPx,
      DEFAULT_FLOATING_TOOLTIP_PADDING_PX,
    ),
    minWidthPx: resolveNonNegativePixelOption(
      options.minWidthPx,
      DEFAULT_FLOATING_TOOLTIP_MIN_WIDTH_PX,
    ),
    minMaxWidthPx: resolveNonNegativePixelOption(
      options.minMaxWidthPx,
      DEFAULT_FLOATING_TOOLTIP_MIN_MAX_WIDTH_PX,
    ),
  };
}

function resolveFloatingTooltipMaxWidth(
  availableWidth: number,
  options: ResolvedFloatingTooltipOptions,
): number {
  const safeAvailableWidth = Number.isFinite(availableWidth)
    ? Math.max(0, availableWidth)
    : 0;
  return Math.max(
    safeAvailableWidth,
    options.minWidthPx,
    options.minMaxWidthPx,
  );
}

function resolveNonNegativePixelOption(
  value: number | undefined,
  fallback: number,
): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.max(0, value);
}

function normalizeFloatingTooltipPlacement(
  placement: Placement,
): FloatingTooltipPlacement {
  return placement.startsWith("bottom") ? "bottom" : "top";
}

function toCssPx(value: number): string {
  return `${roundCssPixel(value)}px`;
}

function roundCssPixel(value: number): number {
  if (value === 0) return 0;
  return Math.round(value * 100) / 100;
}
